import React, { useCallback, useEffect, useRef } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useEnrollment } from '../context';
import { useBankList } from '../hooks/use-bank-list';
import { useEnrollmentInitiation } from '../hooks/use-enrollment-initiation';
import { CpfInput } from '../components/cpf-input';
import { BankSelector } from '../components/bank-selector';
import { isCpfValid } from '../utils/cpf-validation';
import {
  Container,
  Title,
  Subtitle,
  Form,
  InfoBanner,
  InfoIcon,
  InfoText,
  SubmitButton,
  TermsText,
  TermsLink,
} from './bank-linking-screen.styles';

interface Props {
  readonly bankId: string | null | undefined;
  readonly identification: string | null | undefined;
  readonly omitCpfField: boolean;
  readonly omitBankField: boolean;
  readonly loading: boolean;
}

export function BankLinkingScreen({ bankId, identification, omitCpfField, omitBankField, loading: prefillLoading }: Props): ReactNode {
  const { data, dispatch } = useEnrollment();
  const { banks, loading: banksLoading, error: banksError } = useBankList();
  const initiateEnrollment = useEnrollmentInitiation();
  const cpfPrefillAppliedRef = useRef<boolean>(false);
  const bankPrefillAppliedRef = useRef<boolean>(false);
  const isInitiating = data.state === 'INITIATING';

  useEffect(() => {
    if (identification && !cpfPrefillAppliedRef.current && !data.cpf) {
      cpfPrefillAppliedRef.current = true;
      dispatch({ type: 'SET_CPF', cpf: identification });
    }

    if (bankId && !bankPrefillAppliedRef.current && !data.bankId) {
      if (omitBankField) {
        bankPrefillAppliedRef.current = true;
        dispatch({
          type: 'SET_BANK',
          bankId,
          bankName: '',
          bankLogoUrl: '',
        });
      } else if (!banksLoading && banks.length > 0) {
        const matchedBank = banks.find(bank => bank.code === bankId);
        if (matchedBank) {
          bankPrefillAppliedRef.current = true;
          dispatch({
            type: 'SET_BANK',
            bankId: matchedBank.code,
            bankName: matchedBank.name,
            bankLogoUrl: matchedBank.logo_url || '',
          });
        }
      }
    }
  }, [bankId, identification, omitBankField, banksLoading, banks, data.cpf, data.bankId, dispatch]);

  const handleCpfChange = useCallback((rawDigits: string) => {
    dispatch({ type: 'SET_CPF', cpf: rawDigits });
  }, [dispatch]);

  const handleBankSelect = useCallback((bankId: string, bankName: string, bankLogoUrl: string) => {
    dispatch({ type: 'SET_BANK', bankId, bankName, bankLogoUrl });
  }, [dispatch]);

  const isCpfPrefilled = cpfPrefillAppliedRef.current && !!identification;
  const isBankPrefilled = bankPrefillAppliedRef.current && !!bankId;

  const cpfDigits = data.cpf.replace(/\D/g, '');
  const hasValidCpf = omitCpfField || isCpfValid(cpfDigits);
  const hasRequiredBank = omitBankField || data.bankId !== '';
  const isFormValid = hasValidCpf && hasRequiredBank;
  const canSubmit = isFormValid && !isInitiating;
  const hasVisibleFields = !omitCpfField || !omitBankField;
  const subtitle = hasVisibleFields
    ? 'Escolha sua instituição e informe seu CPF para continuar'
    : 'Confirme para ser redirecionado à sua instituição financeira.';

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canSubmit) {
      initiateEnrollment();
    }
  }, [canSubmit, initiateEnrollment]);

  if (prefillLoading) {
    return (
      <Container>
        <Title>Vincule sua conta bancária</Title>
        <Subtitle>Preparando o formulário de vinculação.</Subtitle>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Vincule sua conta bancária</Title>
      <Subtitle>{subtitle}</Subtitle>

      <Form onSubmit={handleSubmit}>
        {!omitCpfField && (
          <CpfInput value={data.cpf} onChange={handleCpfChange} disabled={isCpfPrefilled} />
        )}
        {!omitBankField && (
          <BankSelector
            banks={banks}
            selectedBankId={data.bankId}
            onSelect={handleBankSelect}
            loading={banksLoading}
            error={banksError}
            disabled={isBankPrefilled}
          />
        )}

        <InfoBanner>
          <InfoIcon>ℹ</InfoIcon>
          <InfoText>
            Você será redirecionado para a instituição selecionada para confirmar a operação e voltará automaticamente.
          </InfoText>
        </InfoBanner>

        <SubmitButton type="submit" disabled={!canSubmit}>
          {isInitiating ? 'Vinculando...' : 'Vincular conta'}
        </SubmitButton>

        <TermsText>
          Ao continuar você aceita os <TermsLink href="https://www.ebanx.com/br/termos" target="_blank" rel="noopener noreferrer">termos de uso</TermsLink> do EBANX.
        </TermsText>
      </Form>
    </Container>
  );
}
